import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings as SettingsIcon, 
  User, 
  Shield, 
  Bell, 
  Database, 
  Key, 
  Globe, 
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  Info,
  Building2,
  Plus,
  Edit,
  Trash2,
  Upload,
  FileText
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    organization: user?.organization || '',
    userType: user?.user_type || '',
    roles: user?.roles || []
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [securitySettings, setSecuritySettings] = useState({
    mfaEnabled: user?.mfa_required || false,
    sessionTimeout: 14400, // 4 hours
    loginNotifications: true,
    auditLogging: true
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    complianceAlerts: true,
    securityAlerts: true,
    systemUpdates: false,
    maintenanceAlerts: true
  });

  const [systemInfo, setSystemInfo] = useState({});
  const [isLoadingSystem, setIsLoadingSystem] = useState(false);

  // FFL Management state
  const [ffls, setFfls] = useState([]);
  const [isLoadingFFLs, setIsLoadingFFLs] = useState(false);
  const [showAddFFL, setShowAddFFL] = useState(false);
  const [editingFFL, setEditingFFL] = useState(null);
  const [newFFLData, setNewFFLData] = useState({
    ffl_number: '',
    business_name: '',
    business_type: '',
    license_type: '',
    expiration_date: '',
    status: 'Active',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    contact_email: '',
    contact_phone: ''
  });

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'system', name: 'System', icon: Database },
    { id: 'ffl', name: 'FFL Management', icon: Building2 }
  ];

  // Load profile and system data on component mount
  useEffect(() => {
    loadProfileData();
    loadSystemInfo();
    loadFFLs();
  }, []);

  // Load FFLs when FFL tab is active
  useEffect(() => {
    if (activeTab === 'ffl') {
      loadFFLs();
    }
  }, [activeTab]);

  const loadProfileData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/settings/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData({
          username: data.username || '',
          email: data.email || '',
          organization: data.organization || '',
          userType: data.user_type || '',
          roles: data.roles || []
        });
      }
    } catch (error) {
      console.error('Failed to load profile data:', error);
    }
  };

  const loadSystemInfo = async () => {
    setIsLoadingSystem(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/settings/system', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSystemInfo(data);
      }
    } catch (error) {
      console.error('Failed to load system info:', error);
    } finally {
      setIsLoadingSystem(false);
    }
  };

  const handleExportData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/settings/export-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ format: 'json' }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create and download the file
        const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dark-matter-data-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setMessage('Data exported successfully');
        setMessageType('success');
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to export data');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    }
  };

  // FFL Management Functions
  const loadFFLs = async () => {
    try {
      setIsLoadingFFLs(true);
      const token = localStorage.getItem('token');
      // Using the existing ffl service endpoint
      const response = await fetch('http://localhost:5000/ffl/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Format FFLs array for display
        if (data.ffls) {
          setFfls(data.ffls);
        }
      }
    } catch (error) {
      console.error('Failed to load FFLs:', error);
    } finally {
      setIsLoadingFFLs(false);
    }
  };

  const handleAddFFL = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/ffl/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ffl_number: newFFLData.ffl_number,
          business_name: newFFLData.business_name,
          business_type: newFFLData.business_type,
          license_type: newFFLData.license_type,
          expiration_date: newFFLData.expiration_date,
          status: newFFLData.status,
          address: {
            street: newFFLData.address_street,
            city: newFFLData.address_city,
            state: newFFLData.address_state,
            zip: newFFLData.address_zip
          },
          contact: {
            email: newFFLData.contact_email,
            phone: newFFLData.contact_phone
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('FFL added successfully');
        setMessageType('success');
        setShowAddFFL(false);
        setNewFFLData({
          ffl_number: '',
          business_name: '',
          business_type: '',
          license_type: '',
          expiration_date: '',
          status: 'Active',
          address_street: '',
          address_city: '',
          address_state: '',
          address_zip: '',
          contact_email: '',
          contact_phone: ''
        });
        loadFFLs();
      } else {
        setMessage(data.error || 'Failed to add FFL');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFFL = async (fflNumber) => {
    if (!window.confirm('Are you sure you want to delete this FFL?')) {
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`http://localhost:5000/ffl/${fflNumber}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.ok) {
        setMessage('FFL deleted successfully');
        setMessageType('success');
        loadFFLs();
      } else {
        setMessage('Failed to delete FFL');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearCache = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/settings/clear-cache', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMessage('Cache cleared successfully');
        setMessageType('success');
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to clear cache');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/settings/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: profileData.email,
          organization: profileData.organization
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Profile updated successfully');
        setMessageType('success');
      } else {
        setMessage(data.error || 'Failed to update profile');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage('New passwords do not match');
      setMessageType('error');
      return;
    }

    if (passwordData.newPassword.length < 10) {
      setMessage('Password must be at least 10 characters long');
      setMessageType('error');
      return;
    }

    setIsLoading(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/settings/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Password changed successfully');
        setMessageType('success');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setMessage(data.error || 'Failed to change password');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecurityUpdate = async () => {
    setIsLoading(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/settings/security', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(securitySettings),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Security settings updated successfully');
        setMessageType('success');
      } else {
        setMessage(data.error || 'Failed to update security settings');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setIsLoading(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/settings/notifications', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(notificationSettings),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Notification settings updated successfully');
        setMessageType('success');
      } else {
        setMessage(data.error || 'Failed to update notification settings');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderProfileTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Profile Information</h3>
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                value={profileData.username}
                onChange={(e) => setProfileData({...profileData, username: e.target.value})}
                className="input-field w-full"
                disabled
              />
              <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                className="input-field w-full"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Organization
            </label>
            <input
              type="text"
              value={profileData.organization}
              onChange={(e) => setProfileData({...profileData, organization: e.target.value})}
              className="input-field w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                User Type
              </label>
              <input
                type="text"
                value={profileData.userType}
                className="input-field w-full"
                disabled
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Roles
              </label>
              <input
                type="text"
                value={profileData.roles.join(', ')}
                className="input-field w-full"
                disabled
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </motion.div>
  );

  const renderSecurityTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Password Change */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Change Password</h3>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                className="input-field w-full pr-10"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                className="input-field w-full pr-10"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                className="input-field w-full pr-10"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary flex items-center"
          >
            <Key className="w-4 h-4 mr-2" />
            {isLoading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Security Settings */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Security Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Multi-Factor Authentication</h4>
              <p className="text-sm text-gray-400">Add an extra layer of security to your account</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={securitySettings.mfaEnabled}
                onChange={(e) => setSecuritySettings({...securitySettings, mfaEnabled: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Login Notifications</h4>
              <p className="text-sm text-gray-400">Get notified when someone logs into your account</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={securitySettings.loginNotifications}
                onChange={(e) => setSecuritySettings({...securitySettings, loginNotifications: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Audit Logging</h4>
              <p className="text-sm text-gray-400">Track all account activities for security</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={securitySettings.auditLogging}
                onChange={(e) => setSecuritySettings({...securitySettings, auditLogging: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <button
            onClick={handleSecurityUpdate}
            disabled={isLoading}
            className="btn-primary flex items-center"
          >
            <Shield className="w-4 h-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Security Settings'}
          </button>
        </div>
      </div>
    </motion.div>
  );

  const renderNotificationsTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Email Notifications</h4>
              <p className="text-sm text-gray-400">Receive notifications via email</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationSettings.emailNotifications}
                onChange={(e) => setNotificationSettings({...notificationSettings, emailNotifications: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Compliance Alerts</h4>
              <p className="text-sm text-gray-400">Get notified about compliance issues</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationSettings.complianceAlerts}
                onChange={(e) => setNotificationSettings({...notificationSettings, complianceAlerts: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Security Alerts</h4>
              <p className="text-sm text-gray-400">Get notified about security events</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationSettings.securityAlerts}
                onChange={(e) => setNotificationSettings({...notificationSettings, securityAlerts: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">System Updates</h4>
              <p className="text-sm text-gray-400">Get notified about system updates</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationSettings.systemUpdates}
                onChange={(e) => setNotificationSettings({...notificationSettings, systemUpdates: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Maintenance Alerts</h4>
              <p className="text-sm text-gray-400">Get notified about scheduled maintenance</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationSettings.maintenanceAlerts}
                onChange={(e) => setNotificationSettings({...notificationSettings, maintenanceAlerts: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <button
            onClick={handleNotificationUpdate}
            disabled={isLoading}
            className="btn-primary flex items-center"
          >
            <Bell className="w-4 h-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Notification Settings'}
          </button>
        </div>
      </div>
    </motion.div>
  );

  const renderSystemTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">System Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Platform Version</span>
              <span className="text-white font-mono">{systemInfo.platform_version || 'Loading...'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Database Version</span>
              <span className="text-white font-mono">{systemInfo.database_version || 'Loading...'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Last Backup</span>
              <span className="text-white font-mono">{systemInfo.last_backup ? new Date(systemInfo.last_backup).toLocaleString() : 'Loading...'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Storage Used</span>
              <span className="text-white font-mono">{systemInfo.storage_used || 'Loading...'} / {systemInfo.storage_total || 'Loading...'}</span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Uptime</span>
              <span className="text-white font-mono">{systemInfo.uptime || 'Loading...'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Active Sessions</span>
              <span className="text-white font-mono">{systemInfo.active_sessions || 'Loading...'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">API Calls Today</span>
              <span className="text-white font-mono">{systemInfo.api_calls_today?.toLocaleString() || 'Loading...'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Security Score</span>
              <span className="text-green-400 font-mono">{systemInfo.security_score || 'Loading...'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Data Management</h3>
        <div className="space-y-4">
          <button 
            onClick={handleExportData}
            className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-left"
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium">Export Data</h4>
                <p className="text-sm text-gray-400">Download your data in various formats</p>
              </div>
              <Database className="w-5 h-5 text-gray-400" />
            </div>
          </button>
          
          <button 
            onClick={handleClearCache}
            className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-left"
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium">Clear Cache</h4>
                <p className="text-sm text-gray-400">Clear temporary data and improve performance</p>
              </div>
              <Database className="w-5 h-5 text-gray-400" />
            </div>
          </button>
        </div>
      </div>
    </motion.div>
  );

  const renderFFLTab = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with Add Button */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">FFL Management</h3>
          <button
            onClick={() => setShowAddFFL(!showAddFFL)}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add FFL
          </button>
        </div>
        <p className="text-sm text-gray-400">
          Manage your Federal Firearms Licenses and compliance records
        </p>
      </div>

      {/* Add FFL Form */}
      {showAddFFL && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h4 className="text-md font-semibold text-white mb-4">Add New FFL Record</h4>
          <form onSubmit={handleAddFFL} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  FFL Number *
                </label>
                <input
                  type="text"
                  value={newFFLData.ffl_number}
                  onChange={(e) => setNewFFLData({...newFFLData, ffl_number: e.target.value})}
                  className="input-field w-full"
                  placeholder="12345678"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={newFFLData.business_name}
                  onChange={(e) => setNewFFLData({...newFFLData, business_name: e.target.value})}
                  className="input-field w-full"
                  placeholder="My Gun Store"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Business Type *
                </label>
                <select
                  value={newFFLData.business_type}
                  onChange={(e) => setNewFFLData({...newFFLData, business_type: e.target.value})}
                  className="input-field w-full"
                  required
                >
                  <option value="">Select type...</option>
                  <option value="Type 01">Type 01 - Dealer</option>
                  <option value="Type 02">Type 02 - Pawnbroker</option>
                  <option value="Type 03">Type 03 - Collector</option>
                  <option value="Type 06">Type 06 - Manufacturer</option>
                  <option value="Type 07">Type 07 - Manufacturer (non-firearm)</option>
                  <option value="Type 08">Type 08 - Importer</option>
                  <option value="Type 09">Type 09 - Dealer in destructive devices</option>
                  <option value="Type 10">Type 10 - Manufacturer of destructive devices</option>
                  <option value="Type 11">Type 11 - Importer of destructive devices</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={newFFLData.status}
                  onChange={(e) => setNewFFLData({...newFFLData, status: e.target.value})}
                  className="input-field w-full"
                >
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Expired">Expired</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={newFFLData.expiration_date}
                  onChange={(e) => setNewFFLData({...newFFLData, expiration_date: e.target.value})}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  License Type
                </label>
                <input
                  type="text"
                  value={newFFLData.license_type}
                  onChange={(e) => setNewFFLData({...newFFLData, license_type: e.target.value})}
                  className="input-field w-full"
                  placeholder="Federal Firearms License"
                />
              </div>
            </div>

            <div className="border-t border-gray-700 pt-4">
              <h5 className="text-sm font-semibold text-gray-300 mb-3">Address</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={newFFLData.address_street}
                    onChange={(e) => setNewFFLData({...newFFLData, address_street: e.target.value})}
                    className="input-field w-full"
                    placeholder="123 Main St"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={newFFLData.address_city}
                    onChange={(e) => setNewFFLData({...newFFLData, address_city: e.target.value})}
                    className="input-field w-full"
                    placeholder="Phoenix"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={newFFLData.address_state}
                    onChange={(e) => setNewFFLData({...newFFLData, address_state: e.target.value})}
                    className="input-field w-full"
                    placeholder="AZ"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={newFFLData.address_zip}
                    onChange={(e) => setNewFFLData({...newFFLData, address_zip: e.target.value})}
                    className="input-field w-full"
                    placeholder="85001"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-4">
              <h5 className="text-sm font-semibold text-gray-300 mb-3">Contact</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newFFLData.contact_email}
                    onChange={(e) => setNewFFLData({...newFFLData, contact_email: e.target.value})}
                    className="input-field w-full"
                    placeholder="info@gunstore.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={newFFLData.contact_phone}
                    onChange={(e) => setNewFFLData({...newFFLData, contact_phone: e.target.value})}
                    className="input-field w-full"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {isLoading ? 'Saving...' : 'Save FFL'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddFFL(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FFL List */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h4 className="text-md font-semibold text-white mb-4">Your FFLs</h4>
        {isLoadingFFLs ? (
          <div className="text-center py-8 text-gray-400">Loading FFLs...</div>
        ) : ffls.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-600" />
            <p>No FFL records found</p>
            <p className="text-sm mt-2">Click "Add FFL" to create your first record</p>
          </div>
        ) : (
          <div className="space-y-3">
            {ffls.map((ffl, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Building2 className="w-5 h-5 text-blue-400" />
                      <h5 className="text-white font-medium">{ffl.ffl_number || ffl.business_name}</h5>
                      <span className={`px-2 py-1 rounded text-xs ${
                        ffl.status === 'Active' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {ffl.status || 'Active'}
                      </span>
                    </div>
                    {ffl.business_name && (
                      <p className="text-sm text-gray-400">{ffl.business_name}</p>
                    )}
                    {ffl.business_type && (
                      <p className="text-sm text-gray-400">Type: {ffl.business_type}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingFFL(ffl)}
                      className="p-2 hover:bg-gray-600 rounded-lg"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4 text-blue-400" />
                    </button>
                    <button
                      onClick={() => handleDeleteFFL(ffl.ffl_number)}
                      className="p-2 hover:bg-gray-600 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
          <p className="text-gray-400">Manage your account settings and preferences</p>
        </div>

        {/* Message */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-lg flex items-center ${
              messageType === 'success' 
                ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                : 'bg-red-500/10 border border-red-500/20 text-red-400'
            }`}
          >
            {messageType === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-3" />
            ) : (
              <AlertTriangle className="w-5 h-5 mr-3" />
            )}
            {message}
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === tab.id
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <IconComponent className="w-4 h-4 mr-3" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'security' && renderSecurityTab()}
            {activeTab === 'notifications' && renderNotificationsTab()}
            {activeTab === 'system' && renderSystemTab()}
            {activeTab === 'ffl' && renderFFLTab()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
